// Per-property informational copy that renders below the Checkfront widget
// on each /book-now/{slug} page. Each entry is ~300+ words to give crawlers
// (and guests) meaningful content beyond the booking iframe.

export interface BookingFaq {
  q: string;
  a: string;
}

export interface BookingInfoContent {
  heading: string;
  overview: string[];
  included: string[];
  gettingHere: string;
  planYourDay: string;
  faqs: BookingFaq[];
}

export const BOOKING_INFO: Record<string, BookingInfoContent> = {
  "sons-guadalupe": {
    heading: "Day cabanas, picnic tables & tubing on the Guadalupe",
    overview: [
      "Son's Guadalupe is our flagship riverside property on River Road in New Braunfels — 22 private cabanas spread along a shaded stretch of the Guadalupe River with direct, walk-in river access. Reservations are date- and unit-specific, so the spot you book is the spot that's waiting for you when you arrive: no scrambling for shade, no fighting for parking, and no shared tables.",
      "Most families come for a full day. You'll have your own basecamp with a picnic table, a charcoal grill, and ceiling fans, and tubing is just steps away. Capacity at the property is intentionally limited so the river never feels packed, even on summer weekends.",
    ],
    included: [
      "Private covered cabana (or in-water picnic table) reserved to your group",
      "Picnic table and charcoal BBQ grill at every cabana",
      "Dedicated parking spot (extra spots available for a small add-on fee)",
      "Direct walk-in access to a calm, family-friendly stretch of the Guadalupe",
      "Clean restrooms, changing areas, and Wi-Fi onsite",
      "Tube rentals available to add — shuttle is built into the float",
    ],
    gettingHere:
      "We're at 7308 River Road, about 12 minutes from downtown New Braunfels and roughly an hour from both San Antonio and Austin. Use the on-site entrance signage rather than mapping to neighboring properties — River Road has several outfitters close together and GPS sometimes drops pins at the wrong driveway. Onsite parking is included with every cabana, so you can skip the offsite shuttle lots that fill up by mid-morning on weekends.",
    planYourDay:
      "Arrival is typically late morning; check-in is fast and you can head straight to your cabana. Pack a cooler with food and drinks (glass and styrofoam are not allowed on the river), float for as long as you like, and use your cabana as a home base between trips down. The float ends at the property, so there's no long walk back to your car at the end of the day.",
    faqs: [
      {
        q: "How many people does a cabana fit?",
        a: "Cabanas are sized for up to 12 guests, with 8 guests included in the base rate. Picnic-table rentals include 6 guests and 1 car. Larger groups are welcome — just add extra guests at checkout.",
      },
      {
        q: "Do I need to bring tubes?",
        a: "No. You can add tube and cooler-tube rentals to your booking and pick them up onsite. We also run a shuttle so you don't have to figure out a put-in or take-out.",
      },
      {
        q: "What if the weather turns?",
        a: "See our cancellation policy page for the current weather-hold and reschedule terms. Light rain rarely closes the river; we'll reach out directly if conditions change.",
      },
    ],
  },

  "river-ranch": {
    heading: "Cabanas & day rentals at Son's River Ranch",
    overview: [
      "Son's River Ranch sits on a wide, gentle bend of the Guadalupe River with grassy lawns, big shade trees, and a quieter feel than the busier stretches of River Road. The day-rental layout gives every group its own cabana or covered picnic spot with the river just a few steps away — perfect for families with little kids, multi-generational groups, and anyone who wants room to spread out.",
      "Reservations are unit-specific, so the cabana or picnic table you select online is reserved to your group for the full day. There's no first-come scramble for shade.",
    ],
    included: [
      "Private cabana or covered picnic table reserved to your party",
      "Picnic table seating and shaded riverside lounging space",
      "Onsite parking (add extra spots at checkout if needed)",
      "Direct river access to a calm, swimmable stretch of the Guadalupe",
      "Restrooms, changing areas, and trash service onsite",
      "Tubing add-ons with shuttle service available",
    ],
    gettingHere:
      "Son's River Ranch is on the New Braunfels side of the Guadalupe corridor, easy to reach from I-35 or Highway 46. We recommend pulling up directions in your map app the day before — and entering 'Son's River Ranch' specifically — to avoid being routed to a neighboring outfitter. Onsite parking is included so you don't need to use a public lot or pay a shuttle company.",
    planYourDay:
      "Most groups arrive mid-morning, unpack at their cabana, and float at their own pace through the afternoon. Pack a cooler with non-glass drinks and snacks, throw a few burgers on the grill at lunch, and use the cabana as your basecamp between floats. The property closes in the evening so you can pack up unhurried.",
    faqs: [
      {
        q: "Is it good for kids?",
        a: "Yes — the river bend is wide and shallow at the bank, which makes it one of our most family-friendly properties. Life jackets are strongly recommended for non-swimmers and are available to rent or bring.",
      },
      {
        q: "Can I bring food and drinks?",
        a: "Absolutely. Coolers are encouraged. Glass containers and styrofoam are not allowed anywhere on the river or property.",
      },
      {
        q: "Do you have overnight options here?",
        a: "Yes — see the Son's River Ranch overnight booking page for glamping bell tents and tent camping with bathhouses and fire pits.",
      },
    ],
  },

  "blue-river-camp": {
    heading: "Day visits at Son's Blue River Camp",
    overview: [
      "Son's Blue River Camp is a relaxed, family-run riverside property on the upper Guadalupe near New Braunfels. It's known for clear, blue-green water, big cypress trees, and a calm, shaded float that's ideal for families and first-time tubers. Day-rental guests get a reserved cabana or covered picnic spot with river access, parking, and the use of restrooms and changing areas.",
      "Because Blue River Camp limits how many groups can book each day, the property never feels crowded — even in peak summer.",
    ],
    included: [
      "Reserved private cabana or covered picnic table",
      "Onsite parking included with every booking",
      "Direct walk-in access to the Guadalupe River",
      "Picnic seating, shade, and clean restrooms",
      "Optional tube and cooler-tube rentals at checkout",
      "Friendly, family-run staff onsite all day",
    ],
    gettingHere:
      "Blue River Camp is along the River Road corridor near New Braunfels — about an hour from San Antonio or Austin. Use the Blue River Camp address in your map app, not a nearby outfitter's. Onsite parking is included, so you can skip the paid public lots that fill up early on summer weekends.",
    planYourDay:
      "Most guests arrive mid-morning, settle in at their cabana, and float two or three times throughout the day. The shallow, clear water makes for great wading with kids, and the shaded lounging areas are a nice place to dry off and snack between floats. Bring a cooler with non-glass drinks, sunscreen, and water shoes.",
    faqs: [
      {
        q: "Is the water actually blue?",
        a: "Yes — this stretch of the Guadalupe is fed by limestone springs and runs unusually clear, with a blue-green tint that's especially noticeable on sunny days.",
      },
      {
        q: "Are pets allowed?",
        a: "Policies vary by season and property. Reach out to our team at info@sonsgetaways.com before booking if you'd like to bring a dog.",
      },
      {
        q: "How early can we arrive?",
        a: "Check-in time is listed on your booking confirmation. Arriving right at open gets you settled before the day fills up.",
      },
    ],
  },

  "rio-cibolo": {
    heading: "Day cabanas on Cibolo Creek",
    overview: [
      "Son's Rio Cibolo is a creekside getaway on Cibolo Creek near Marion, a short drive from New Braunfels and Seguin. It's a quieter, more secluded property than our Guadalupe locations — perfect for groups who want a private day on the water without the crowds. Day rentals include a reserved cabana or covered picnic spot, onsite parking, and direct access to the creek.",
      "The creek is calm and shaded, which makes it a relaxing alternative to busy stretches of the Guadalupe. It's also a great option for families with younger kids who want gentler water.",
    ],
    included: [
      "Reserved cabana or covered picnic table for the day",
      "Picnic seating and shaded creekside space",
      "Onsite parking included with your reservation",
      "Direct walk-in access to Cibolo Creek",
      "Restrooms and changing areas",
      "Easy add-ons at checkout for extra guests or parking",
    ],
    gettingHere:
      "Son's Rio Cibolo is in Marion, TX — about 20 minutes east of New Braunfels and 15 minutes from Seguin. Map to 'Son's Rio Cibolo' specifically; the property has its own entrance. Parking is onsite and included with every booking.",
    planYourDay:
      "Bring a cooler, set up at your cabana, and spend the day swimming, wading, and lounging on the creek. The pace here is slow on purpose — it's a good fit for families, small group celebrations, and anyone who'd rather skip the louder party stretches of the Guadalupe. Glass and styrofoam are not allowed on the creek.",
    faqs: [
      {
        q: "Is there tubing on Cibolo Creek?",
        a: "Cibolo is calmer than the Guadalupe; most guests wade, swim, or float casually rather than running a full tube trip. If tubing is your main goal, our Guadalupe properties are a better match.",
      },
      {
        q: "Can I host a small private event here?",
        a: "Yes — group cabana bookings work well for birthday parties, reunions, and small gatherings. Reach out via the Group Bookings page for larger setups.",
      },
      {
        q: "Are overnight stays available?",
        a: "Yes — see the Son's Rio Cibolo overnight booking page for glamping options on the creek.",
      },
    ],
  },

  "sons-island": {
    heading: "Day rentals on a private spring-fed lake",
    overview: [
      "Son's Island is a private, spring-fed island lake in Seguin, just east of New Braunfels — and it's unlike anything else in our family of properties. Instead of a river float, you get clear, calm lake water surrounded by an island setting with cabanas, picnic spots, and swim areas. Day rentals are reserved per group, so you'll have your own basecamp on the water.",
      "It's a great pick for families with younger kids, groups that don't want to deal with river current, and anyone who'd rather swim and lounge than tube.",
    ],
    included: [
      "Reserved cabana or lakeside picnic table for your group",
      "Direct access to the spring-fed lake",
      "Onsite parking included with every booking",
      "Picnic seating, shade, and clean restrooms",
      "Swimming areas suitable for kids and casual swimmers",
      "Optional add-ons for extra guests at checkout",
    ],
    gettingHere:
      "Son's Island is in Seguin, TX — about 25 minutes east of New Braunfels and easy to reach from I-10. Map to 'Son's Island' so your GPS lands at the right gate. Parking is onsite and included.",
    planYourDay:
      "Most groups arrive mid-morning, claim their cabana, and spend the day swimming, paddling, and lounging on the lake. Pack a cooler with snacks and non-glass drinks. The vibe is calmer and quieter than our river properties — it's a favorite of families with little kids and groups looking for a slower-paced day on the water.",
    faqs: [
      {
        q: "Is there a current like on the river?",
        a: "No — the lake is spring-fed and calm. That makes it easier for kids and less-confident swimmers compared to the Guadalupe.",
      },
      {
        q: "Can I bring my own kayak or paddleboard?",
        a: "Yes, in most cases. Check your booking confirmation or contact us before arrival for the latest watercraft policy.",
      },
      {
        q: "Do you offer overnight stays here?",
        a: "Yes — see the Son's Island overnight booking page for lakeside glamping bell tents.",
      },
    ],
  },

  geronimo: {
    heading: "Day rentals on Geronimo Creek",
    overview: [
      "Son's Geronimo is a quiet, shaded creekside property on Geronimo Creek in Seguin. It's our most laid-back day-rental location — a great option for groups who want gentle creek water, big shade trees, and far fewer crowds than the busy Guadalupe corridor. Day rentals include a reserved cabana or covered picnic spot with onsite parking and direct creek access.",
      "Because the creek is calm, it's especially well suited for families with young kids, multi-generational groups, and anyone looking for a low-key day outside.",
    ],
    included: [
      "Reserved cabana or covered picnic table for your party",
      "Onsite parking included with your booking",
      "Direct creek access for wading, swimming, and lounging",
      "Picnic seating with plenty of shade",
      "Clean restrooms and changing areas",
      "Easy add-ons at checkout for extra guests or parking",
    ],
    gettingHere:
      "Son's Geronimo is in Seguin, TX — roughly 25 minutes east of New Braunfels and very close to I-10. Map to 'Son's Geronimo' directly so your GPS lands at our entrance instead of a nearby outfitter. Parking is included onsite.",
    planYourDay:
      "Arrive mid-morning, settle in at your cabana, and spend the day wading, swimming, and relaxing in the shade. Pack a cooler with non-glass drinks and snacks, bring water shoes for the rocky bottom, and plan to slow down — Geronimo is best enjoyed at a relaxed pace.",
    faqs: [
      {
        q: "Is there tubing here?",
        a: "Geronimo Creek is calmer than the Guadalupe, so most guests wade, swim, and float casually rather than running a long tube trip. For a full tubing day, our Guadalupe properties are a better fit.",
      },
      {
        q: "Is it good for younger kids?",
        a: "Yes — the creek runs gentle and shallow at the bank. Life jackets are recommended for non-swimmers and easy to bring along.",
      },
      {
        q: "Can I rent multiple cabanas for a group?",
        a: "Yes. Reach out via the Group Bookings page if you need to coordinate several units or a larger gathering.",
      },
    ],
  },
};
